import React from 'react';
import { 
  ListItem, 
  ListItemText, 
  Typography, 
  Box, 
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DoneIcon from '@mui/icons-material/Done';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { NotificacaoInterface } from '../../types/NotificacaoInterface';
import { TipoNotificacao } from '../../utils/enums';
import { useNavigate } from 'react-router-dom';

// Função para obter o ícone baseado no tipo de notificação
const obterIconeTipoNotificacao = (tipo: TipoNotificacao) => {
  switch (tipo) {
    case TipoNotificacao.TICKET_CRIADO:
      return '🎫';
    case TipoNotificacao.TICKET_ATUALIZADO:
      return '🔄';
    case TipoNotificacao.TICKET_FECHADO:
      return '✅';
    case TipoNotificacao.TICKET_REABERTO:
      return '🔓';
    case TipoNotificacao.TICKET_ATRIBUIDO:
      return '👤';
    case TipoNotificacao.COMENTARIO_ADICIONADO:
      return '💬';
    case TipoNotificacao.ANEXO_ADICIONADO:
      return '📎';
    default:
      return '📢';
  }
};

interface ItemNotificacaoProps {
  notificacao: NotificacaoInterface;
  onMarcarComoLida: (id: number) => void;
  onNavegar: (notificacao: NotificacaoInterface) => void;
}

const ItemNotificacao: React.FC<ItemNotificacaoProps> = ({
  notificacao,
  onMarcarComoLida,
  onNavegar,
}) => {
  const { id, titulo, tipo, lida, createdAt, idRef } = notificacao;
  const dataFormatada = format(new Date(createdAt), "dd 'de' MMMM 'às' HH:mm", { locale: ptBR });
  const icone = obterIconeTipoNotificacao(tipo as TipoNotificacao);
  const navigate = useNavigate();

  const handleNavegar = () => {
    // Se não estiver lida, marca como lida
    if (!lida) {
      onMarcarComoLida(id);
    }

    // Navega para a página de edição do ticket
    navigate(`/tickets/editar/${idRef}`);
    
    // Chama a função onNavegar passada como prop
    onNavegar(notificacao);
  };

  return (
    <>
      <ListItem
        alignItems="flex-start"
        sx={{
          backgroundColor: lida ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          transition: 'background-color 0.3s',
          borderRadius: '4px',
          mb: 0.5,
          py: 1,
          px: 2,
        }}
        secondaryAction={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!lida && (
              <Tooltip title="Marcar como lida">
                <IconButton 
                  edge="end" 
                  aria-label="marcar como lida" 
                  onClick={() => onMarcarComoLida(id)}
                  size="small"
                >
                  <DoneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Abrir item">
              <IconButton 
                edge="end" 
                aria-label="abrir item" 
                onClick={handleNavegar}
                size="small"
                sx={{ ml: 1 }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        }
      >
        <Box sx={{ mr: 2, fontSize: '1.2rem', display: 'flex', alignItems: 'flex-start', mt: 0.5 }}>{icone}</Box>
        <ListItemText
          primary={
            <Typography
              variant="body2"
              sx={{
                fontWeight: lida ? 'normal' : 'bold',
                pr: 8, // Espaço para os botões
                fontSize: '0.875rem',
                lineHeight: 1.2,
              }}
            >
              {titulo}
            </Typography>
          }
          secondary={
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
            >
              {dataFormatada}
            </Typography>
          }
        />
      </ListItem>
      <Divider variant="inset" component="li" sx={{ ml: 7 }} />
    </>
  );
};

export default ItemNotificacao; 